/**
 * @fileoverview NY Noir-specific utility functions for address formatting and display.
 * Includes hover tooltip generation, address/apartment formatting, and region/location labels.
 * @module utils/nynfuncs
 */

import { MAP_CONSTANTS, REGEXES } from '../constants.js';
import { escapeHtml } from './jrfuncs.js';


// ==========================================
// HTML HELPERS
// ==========================================

/**
 * Calculate HTML for hover tooltip from feature properties
 * @param {Object} properties - Feature properties
 * @param {string} properties.dName - Display name
 * @param {string} properties.address - Street address
 * @param {string} properties.apt - Apartment/unit number
 * @returns {string} HTML string for hover tooltip
 */
export function calculateHoverTextHtml(properties) {
    // an imported custom pin needs only a uid and a point, so its name and address may be missing
    // (or not text): with both missing, undefined == undefined sent the Canvas test below into
    // address.startsWith, which threw on every pointermove over the pin
    let dName = (typeof properties.dName === 'string') ? properties.dName : '';
    const address = (typeof properties.address === 'string') ? properties.address : '';
    let fullAddress = formatAddressAptInParens(address, properties.apt);
    let lead = properties.lead;
    const optionLeadOnNewLine = true;
    const userLabel = properties.userLabel;

    // for things like Canvas
    if (dName && dName == address) {
        if (address.startsWith('Canvas area around ')) {
            dName = 'Canvas ' + address.substring(19);
            fullAddress = 'Special action';
        } else {
            fullAddress = '';
        }
    }

    if (lead == address) {
        // this can happen in SHCD, no need to show lead & address
        fullAddress = '';
    }
    if (lead=='n/a') {
        lead = '';
    }

    if (userLabel) {
        // (a pin with no name of its own shows just its label, not " (label)")
        if (dName == MAP_CONSTANTS.USER_BOOKMARK_NAME || !dName) {
            dName = userLabel;
        } else {
            dName += ' (' + userLabel + ')';
        }
    }

    // name (every field is text -- data or the user's own/imported bookmark label -- so escape it)
    let hoverTextHtml = `<span class="hover-dName">${escapeHtml(dName)}</span>`;

    // address
    if (fullAddress) {
        hoverTextHtml += `<br/><span class="hover-address">${escapeHtml(fullAddress)}</span>`;
    }
    // lead
    if (optionLeadOnNewLine) {
        hoverTextHtml += `<br/><span class="hover-lead">${escapeHtml(lead)}</span>`;
    } else {
        hoverTextHtml += ' <span class="hover-lead">[' + escapeHtml(lead) + ']</span>';
    }

    return hoverTextHtml;
}


/**
 * Formats an item's address nicely for display.
 * Includes apartment number and region/location label if available.
 * @param {Object} item - The data item with address properties
 * @param {string} item.address - Street address
 * @param {string} [item.apt] - Apartment/unit number
 * @param {string} [item.jregion] - Region identifier
 * @param {string} [item.loclabel] - Location label
 * @returns {string} Formatted address string
 */
export function formatAddressForItemNicely(item) {
    // ATTN: TODO - support unlisted addresses
    // start with address
    let address = item.address;
    // add apt
    if (item.apt) {
        address += ', ' +formatAptAsString(item.apt, false);
    }
    // add jregion and loclabel
    if (item.jregion) {
        address += ` [${item.jregion}-${item.loclabel}]`;
    }
    return address
}


/**
 * Formats an item's address nicely for display.
 * Includes apartment number and region/location label if available.
 * @param {Object} item - The data item with address properties
 * @param {string} item.address - Street address
 * @param {string} [item.apt] - Apartment/unit number
 * @param {string} [item.jregion] - Region identifier
 * @param {string} [item.loclabel] - Location label
 * @returns {string} Formatted address string
 */
export function formatAddressForItemNicelyCheckOverride(item, flagAddRegion) {
    // ATTN: TODO - support unlisted addresses
    // start with address
    let address = item.addressOverride ?? item.address;
    // add apt
    if (item.apt) {
        if (address) {
            address += (address ? ', ' : '') + formatAptAsString(item.apt, false);
        } else {
            address = formatAptAsString(item.apt, true);
        }
    }
    // add jregion and loclabel
    if (item.jregion && flagAddRegion) {
        address += ` [${item.jregion}-${item.loclabel}]`;
    }
    return address
}






/**
 * Format apartment number/unit with "apt." prefix if it's a simple numeric value
 * @param {string} apt - Apartment/unit number
 * @returns {string} Formatted apartment string
 */
export function formatAptAsString(apt, flagTitleCase) {
    if (!apt) {
        return apt;
    }
    // imported data may carry a number (apt: 5), whose [0] is undefined (toUpperCase below threw)
    apt = String(apt);
    const c = apt[0];
    if ((c >= '0' && c <= '9') && (apt.length <= 3)) {
        apt = "apt. " + apt;
    } else {
        if (apt=='pnt') {
            apt = 'Penthouse';
        }
    }

    apt = apt[0].toUpperCase() + apt.slice(1);
    return apt;
}

/**
 * Format address with apartment using configurable style.
 * Consolidates formatting logic for different display contexts.
 * @param {string} address - Street address
 * @param {string} apt - Apartment/unit number
 * @param {Object} [options={}] - Formatting options
 * @param {boolean} [options.useParens=false] - Wrap apt in parentheses instead of comma
 * @param {string} [options.separator=', '] - Separator when not using parens
 * @returns {string} Formatted address string
 */
export function formatAddressWithApt(address, apt, options = {}) {
    if (!address) {
        return address;
    }
    const aptString = formatAptAsString(apt, false);
    if (!aptString) {
        return address;
    }

    const { useParens = false, separator = ', ' } = options;
    return useParens
        ? `${address} (${aptString})`
        : `${address}${separator}${aptString}`;
}

/**
 * Format full address with apartment number, in parens
 * @param {string} address - Street address
 * @param {string} apt - Apartment/unit number
 * @returns {string} Combined address with apartment in parentheses
 */
export function formatAddressAptInParens(address, apt) {
    return formatAddressWithApt(address, apt, { useParens: true });
}



/**
 * Format full address with apartment number, with comma separator
 * @param {string} address - Street address
 * @param {string} apt - Apartment/unit number
 * @returns {string} Combined address with comma separator
 */
export function formatAddressAptWithComma(address, apt) {
    return formatAddressWithApt(address, apt, { useParens: false, separator: ', ' });
}



/**
 * Format region and location label into a combined string.
 * @param {string} jregion - Region identifier (e.g., 'MH', 'BK')
 * @param {string|number} [loclabel] - Location label number; a missing one reads as '??', the
 *   "unknown block" the app writes on the custom pins it creates (an imported or hand-edited pin can
 *   carry a region without a location label, and the export used to throw on it)
 * @param {string} prefix - Prefix to add before the formatted string
 * @param {string} suffix - Suffix to add after the formatted string
 * @param {string} defaultVal - Default value to return if jregion is empty
 * @returns {string} Formatted region-location string or defaultVal
 */
export function formatJregionLoclabel(jregion, loclabel, prefix, suffix, defaultVal) {
    if (!jregion) {
        return defaultVal;
    }
    const loclabelText = (loclabel === null || loclabel === undefined) ? '??' : String(loclabel);
    const txt = `${prefix}${jregion}${loclabelText.padStart(2, '0')}${suffix}`
    return txt;
}

/**
 * Format region and location label with " @ " prefix.
 * Convenience wrapper for formatJregionLoclabel.
 * @param {string} jregion - Region identifier
 * @param {string} loclabel - Location label number
 * @returns {string} Formatted string like " @ MH01"
 */
export function formatJregionLoclabelWithAt(jregion, loclabel) {
    return formatJregionLoclabel(jregion, loclabel, ' @ ', '', '')
}


/**
 * Parses a block qstring string into neighborhood code and block number.
 * Supports formats like "MH:123", "MH123", "123:MH", "B:12", etc. (1 or 2 letter neighborhood codes).
 * @param {string} qstring - The block qstring string
 * @returns {Object} Object with jregion and loclabel properties
 */
export function parseBlockQueryString(qstring) {
    let match = null;
    let jregion = null;
    let loclabel = null;

    match = qstring.match(REGEXES.NEIGHBORHOOD_FIRST);
    if (match) {
        [, jregion, loclabel] = match;
    } else {
        match = qstring.match(REGEXES.NEIGHBORHOOD_LAST);
        if (match) {
            [, loclabel, jregion] = match;
        }
    }

    if (jregion) {
        jregion = jregion.toUpperCase();
    }
    if (loclabel) {
        loclabel = loclabel.replace(/^[\s0]+/, '');
    }

    return { jregion, loclabel };
}



// Pre-compiled regex for address sorting - matches house number at start (e.g., "123 " or "12A ")
const HOUSE_NUMBER_REGEX = /^(\d+[a-zA-Z]?)\s+/;

/**
 * Calculates a full address string optimized for alphabetical sorting.
 * Moves the house number to the end so addresses sort by street name first.
 * @param {Object} item - The data item with address properties
 * @param {string} item.address - Street address
 * @param {string} [item.apt] - Apartment/unit number
 * @returns {string} Address formatted for sorting (e.g., "Main Street, 123, apt.2B")
 */
export function calcFullAddressForSorting(item) {
    // at a minimum, return address with apt added; but ideally put street name first, then house number, than apt
    let address = item.address || '';
    if (address.startsWith('Canvas area')) {
        // kludge to sort canvas stuff at end
        return 'ZZZZ_' + address;
    }
    // if the address starts with a house number (e.g., "123 " or "12A "), move it to the end
    const match = address.match(HOUSE_NUMBER_REGEX);
    if (match) {
        const houseNumber = match[1];
        const streetName = address.slice(match[0].length);
        address = streetName + ', ' + houseNumber;
    }
    if (item.apt) {
        address += ', apt.' + item.apt;
    }
    return address;
}


/**
 * Sorts an array of data objects by a specified field name.
 * Uses locale-aware comparison with numeric sorting, or pure numeric comparison.
 * @param {Array} data - Array of objects to sort
 * @param {string} fieldName - The field name to sort by
 * @param {boolean} flagPureNumeric - If true, uses numeric subtraction; if false, uses localeCompare
 * @returns {Array} New sorted array (does not modify original)
 */
export function sortDataByField(data, fieldName, flagPureNumeric) {
    if (flagPureNumeric) {
        return [...data].sort((a, b) => {
            const valA = a[fieldName];
            const valB = b[fieldName];
            // Handle null/undefined - sort them to the end
            /*
            if (valA == null && valB == null) return 0;
            if (valA == null) return 1;
            if (valB == null) return -1;
            */
            return valA - valB;
        });
    }
    const sortedData = [...data].sort((a, b) => {
        const valA = a[fieldName] || '';
        const valB = b[fieldName] || '';
        return valA.localeCompare(valB, undefined, { numeric: true, sensitivity: 'variant' });
    });
    return sortedData;
}


/**
 * Sorts an array of data objects by address, using special address formatting.
 * Addresses are formatted for sorting (street name first, then house number).
 * @param {Array} data - Array of objects to sort
 * @returns {Array} New sorted array
 */
export function sortDataByFieldAddressSpecial(data) {
    const sortedData = [...data].sort((a, b) => {
        const valA = calcFullAddressForSorting(a);
        const valB = calcFullAddressForSorting(b);
        return valA.localeCompare(valB, undefined, { numeric: true, sensitivity: 'variant' });
    });
    return sortedData;
}


// ==========================================
// CATEGORY HELPERS
// ==========================================


/**
 * Returns the final category of a composite nynoir category value.
 * A few places carry composite pcat/pcatg values of the form "<region prefix>|<category>",
 * e.g. "Outer Boroughs>Brooklyn|Transport Depots / Airports / Terminals": the text after the
 * LAST '|' is the category the value means. Non-composite values come back unchanged;
 * null/undefined come back as ''.
 * Used by the icon lookup (ImageHelper.calcIconLookupKeysForItem); the yellow pages
 * category search matches EVERY segment instead (calcCategorySegments). MIRRORED in python by
 * calcIconLookupKeys() in tools/mapDeployTools.py -- change both together.
 * @param {string|null|undefined} value - A pcat or pcatg value
 * @returns {string} The category after the last '|', or the value itself
 */
export function calcCategoryTail(value) {
    if (!value) {
        return '';
    }
    const text = String(value);
    const pipeIndex = text.lastIndexOf('|');
    return (pipeIndex < 0) ? text : text.substring(pipeIndex + 1).trim();
}


/**
 * Returns every category a (possibly composite) nynoir category value is filed under: each
 * '|'-separated segment, trimmed, so "~Off Map>New Jersey|Transport Depots / Airports / Terminals"
 * is listed under BOTH "~Off Map>New Jersey" and "Transport Depots / Airports / Terminals".
 * Used by the yellow pages category search (YellowSearcher.doYellowSearchForPcatg). The icon
 * deliberately comes from the LAST segment only (calcCategoryTail). Non-composite values come
 * back as a one-element list; null/undefined as [].
 * @param {string|null|undefined} value - A pcat or pcatg value
 * @returns {string[]} The non-empty trimmed segments
 */
export function calcCategorySegments(value) {
    if (!value) {
        return [];
    }
    return String(value).split('|').map(part => part.trim()).filter(part => part.length > 0);
}
